import React, { useEffect } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactComponent as ChevronDown } from "../../../assets/Icons/chevron-down.svg";
import { Box } from "@mui/material";
import { Controller } from "react-hook-form";

const ProfessionalLicenseForm = ({
  licensesList,
  statesList,
  onChange,
  control,
  license,
  errors,
  onDelete,
  showDeleteButton,
  index,
  onLicenseChange,
  onSupervisionChange,
  onToggleExpanded,
  watchedLicense,
  isExpanded,
  isCollapsed,
  showValidationErrors,
}) => {
  const hasLicense = watchedLicense?.has_license ?? false;
  const hasSupervision = watchedLicense?.has_supervision ?? false;
  const licenseTypeName =
    licensesList?.find(
      (l) =>
        l.id === license?.license_type_id || license?.supervisor_license_type_id
    )?.name || "License";
  useEffect(() => {}, [hasLicense, hasSupervision, showValidationErrors]);
  const renderLicenseFields = () => (
    <>
      {!hasSupervision && (
        <Grid xs={12} sx={{ ml: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hasLicense}
                onChange={(e) => onLicenseChange(e.target.checked)}
              />
            }
            label="I am currently licensed to practice"
          />
        </Grid>
      )}

      {hasLicense && (
        <>
          <Grid item xs={12}>
            <Controller
              name={`licenses.${index}.license_type_id`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  variant="outlined"
                  label="License Type"
                  fullWidth
                  SelectProps={{
                    IconComponent: () => (
                      <Box
                        sx={{
                          minWidth: 24,
                          width: 24,
                          height: 24,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                        }}
                      >
                        <ChevronDown width={12} height={6} />
                      </Box>
                    ),
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 220,
                          width: 250,
                        },
                      },
                    },
                  }}
                  onChange={onChange}
                  value={license?.license_type_id || ""}
                  error={!!errors?.licenses?.[index]?.license_type_id}
                  helperText={
                    errors?.licenses?.[index]?.license_type_id?.message
                  }
                >
                  <MenuItem value={0} disabled>
                    Select a license type
                  </MenuItem>
                  {licensesList?.map((licenseType) => (
                    <MenuItem key={licenseType.id} value={licenseType.id}>
                      {licenseType.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>

          <Grid item md={6} xs={12}>
            <Controller
              name={`licenses.${index}.license_state`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  variant="outlined"
                  label="License State"
                  fullWidth
                  SelectProps={{
                    IconComponent: () => (
                      <Box
                        sx={{
                          minWidth: 24,
                          width: 24,
                          height: 24,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                        }}
                      >
                        <ChevronDown width={12} height={6} />
                      </Box>
                    ),
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 220,
                          width: 250,
                        },
                      },
                    },
                  }}
                  onChange={onChange}
                  value={license?.license_state || 0}
                  error={!!errors?.licenses?.[index]?.license_state}
                  helperText={errors?.licenses?.[index]?.license_state?.message}
                >
                  <MenuItem value={0} disabled>
                    Select a state
                  </MenuItem>
                  {statesList?.map((state) => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>

          <Grid item md={6} xs={12}>
            <Controller
              name={`licenses.${index}.licensing_board`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="Licensing Board"
                  onChange={onChange}
                  fullWidth
                  error={!!errors?.licenses?.[index]?.licensing_board}
                  helperText={
                    errors?.licenses?.[index]?.licensing_board?.message
                  }
                />
              )}
            />
          </Grid>

          <Grid item md={6} xs={12}>
            <Controller
              name={`licenses.${index}.license_number`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="License Number"
                  onChange={onChange}
                  fullWidth
                  error={!!errors?.licenses?.[index]?.license_number}
                  helperText={
                    errors?.licenses?.[index]?.license_number?.message
                  }
                />
              )}
            />
          </Grid>

          <Grid item md={6} xs={12} sx={{ mb: 3 }}>
            <Controller
              name={`licenses.${index}.license_year`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="License Year (YYYY)"
                  onChange={onChange}
                  fullWidth
                  error={!!errors?.licenses?.[index]?.license_year}
                  helperText={errors?.licenses?.[index]?.license_year?.message}
                />
              )}
            />
          </Grid>
        </>
      )}

      {!hasLicense && (
        <Grid xs={12} sx={{ ml: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hasSupervision}
                onChange={(e) => onSupervisionChange(e.target.checked)}
              />
            }
            label="I am pre-licensure/working under supervision"
          />
        </Grid>
      )}

      {hasSupervision && (
        <>
          <Grid item xs={12}>
            <Controller
              name={`licenses.${index}.supervisor_name`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="Supervisor's Name"
                  onChange={onChange}
                  fullWidth
                  error={!!errors?.licenses?.[index]?.supervisor_name}
                  helperText={
                    errors?.licenses?.[index]?.supervisor_name?.message
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name={`licenses.${index}.supervisor_license_type_id`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  variant="outlined"
                  label="Supervisor's License Type"
                  fullWidth
                  SelectProps={{
                    IconComponent: () => (
                      <Box
                        sx={{
                          minWidth: 24,
                          width: 24,
                          height: 24,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                        }}
                      >
                        <ChevronDown width={12} height={6} />
                      </Box>
                    ),
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 220,
                          width: 250,
                        },
                      },
                    },
                  }}
                  onChange={onChange}
                  value={license?.supervisor_license_type_id || ""}
                  error={
                    !!errors?.licenses?.[index]?.supervisor_license_type_id
                  }
                  helperText={
                    errors?.licenses?.[index]?.supervisor_license_type_id
                      ?.message
                  }
                >
                  <MenuItem value={0} disabled>
                    Select supervisor's license type
                  </MenuItem>
                  {licensesList?.map((licenseType) => (
                    <MenuItem key={licenseType.id} value={licenseType.id}>
                      {licenseType.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name={`licenses.${index}.supervisor_license_state`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  variant="outlined"
                  label="Supervisor's License State"
                  fullWidth
                  SelectProps={{
                    IconComponent: () => (
                      <Box
                        sx={{
                          minWidth: 24,
                          width: 24,
                          height: 24,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                        }}
                      >
                        <ChevronDown width={12} height={6} />
                      </Box>
                    ),
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 220,
                          width: 250,
                        },
                      },
                    },
                  }}
                  onChange={onChange}
                  value={license?.supervisor_license_state || 0}
                  error={!!errors?.licenses?.[index]?.supervisor_license_state}
                  helperText={
                    errors?.licenses?.[index]?.supervisor_license_state?.message
                  }
                >
                  <MenuItem value={0} disabled>
                    Select a state
                  </MenuItem>
                  {statesList?.map((state) => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        </>
      )}

      {!hasLicense && !hasSupervision && showValidationErrors && (
        <Grid item xs={12}>
          <Typography color="error" variant="body2">
            Please select at least one option.
          </Typography>
        </Grid>
      )}

      {showDeleteButton && (
        <Grid item xs={12}>
          <Button variant="outlined" color="error" onClick={() => onDelete()}>
            Delete
          </Button>
        </Grid>
      )}
    </>
  );

  if (isCollapsed) {
    return (
      <Accordion
        expanded={isExpanded}
        onChange={onToggleExpanded}
        sx={{ mt: 2, py: 2, borderRadius: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              mx: 2,
            }}
          >
            <Typography sx={{ fontSize: 18 }}>{licenseTypeName}</Typography>
            {/* <Typography sx={{ fontSize: 18 }}>
              #{index + 1}
            </Typography> */}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {renderLicenseFields()}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Grid container spacing={3} mt={2}>
      {renderLicenseFields()}
    </Grid>
  );
};

export default ProfessionalLicenseForm;
